import React, { useState } from "react";
import { ChevronDown, ChevronRight, ExternalLink, Hotel, Bike, Plane, Car, Utensils } from "lucide-react";
import { fmtUsd, fmtWhen } from "./labels";

const fmtMoney = (amount, currency) => (amount == null ? "—" : new Intl.NumberFormat("es-ES", { style: "currency", currency: currency || "EUR", maximumFractionDigits: 0 }).format(amount));
const KIND_ICON = { hotel: Hotel, activity: Bike, plane: Plane, private_transport: Car, meal: Utensils };

function Metric({ label, value }) {
  return <div><div className="smallcaps">{label}</div><div className="mt-0.5 text-sm tabular">{value ?? "—"}</div></div>;
}

// Proposal price per person/day vs the budget category the traveler picked.
export function vsBudget(perDay, b) {
  if (!perDay || !b?.budget_pp_day_lo) return null;
  const lo = b.budget_pp_day_lo, hi = b.budget_pp_day_hi;
  if (perDay < lo) return { cls: "border-sky-300 text-sky-800 bg-sky-50", text: `${Math.round((perDay / lo - 1) * 100)}% bajo la categoría` };
  if (hi != null && perDay > hi) return { cls: "border-amber-300 text-amber-800 bg-amber-50", text: `+${Math.round((perDay / hi - 1) * 100)}% sobre la categoría` };
  return { cls: "border-emerald-300 text-emerald-800 bg-emerald-50", text: "dentro de la categoría" };
}

function ProposalCard({ p, index, budget }) {
  const vs = vsBudget(p.metrics?.usd_per_person_day, budget);
  const [open, setOpen] = useState(false);
  const [showDays, setShowDays] = useState(false);
  const m = p.metrics || {};
  const lines = (p.pricing?.lines || []).filter((l) => l.amount != null);
  return (
    <div className="border border-clay-300 bg-white" data-testid={`kimkim-proposal-${index}`}>
      <button onClick={() => setOpen(!open)} className="w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-clay-50" data-testid={`kimkim-proposal-${index}-toggle`}>
        {open ? <ChevronDown size={14} className="mt-1 shrink-0"/> : <ChevronRight size={14} className="mt-1 shrink-0"/>}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">{p.title || "Propuesta"}</div>
          <div className="text-[11px] text-clay-600 mt-0.5">
            Enviada {fmtWhen(p.sent_at)}{p.sent_label ? ` · último envío ${p.sent_label}` : ""} · {p.pax || "?"} · {p.dates_raw || "sin fechas"}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-sm tabular font-medium">{fmtUsd(p.total_usd)}</div>
          <div className="text-[11px] text-clay-600 tabular">{m.usd_per_person ? `${fmtUsd(m.usd_per_person)}/pers` : ""}{m.usd_per_person_day ? ` · ${fmtUsd(m.usd_per_person_day)}/pers·día` : ""}</div>
          {vs && <span className={`inline-block mt-1 border px-1.5 text-[10px] ${vs.cls}`} data-testid={`kimkim-proposal-${index}-vs-budget`}>{vs.text}</span>}
        </div>
      </button>
      {open && (
        <div className="border-t border-clay-200 px-4 py-4 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Metric label="Días / hoteles / actividades" value={`${m.days ?? "?"} · ${m.hotels_count ?? 0} · ${m.activities_count ?? 0}`}/>
            <Metric label="Markup" value={m.markup_pct != null ? `${m.markup_pct}%` : "—"}/>
            <Metric label="Coste actividades · hoteles" value={`${fmtMoney(m.activities_eur, "EUR")} · ${fmtMoney(m.hotels_eur, "EUR")}`}/>
            <Metric label="Total EUR · comisión KimKim" value={`${fmtMoney(m.trip_total_eur, "EUR")} · ${fmtMoney(m.commission_eur, "EUR")}`}/>
          </div>
          {lines.length > 0 && (
            <div>
              <div className="smallcaps mb-1">Desglose de precio (solo especialista)</div>
              <table className="w-full text-[12px]" data-testid={`kimkim-proposal-${index}-pricing`}>
                <tbody>
                  {lines.map((l, i) => (
                    <tr key={i} className={`border-t border-clay-100 ${/total|subtotal/i.test(l.label) ? "font-medium" : ""}`}>
                      <td className="py-1 pr-3">{l.label}</td>
                      <td className="py-1 text-right tabular whitespace-nowrap">{fmtMoney(l.amount, l.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {p.pricing?.rate_text && <div className="text-[10px] text-clay-500 mt-1">Cambio {p.pricing.rate_text}</div>}
            </div>
          )}
          {(p.hotels || []).length > 0 && (
            <div>
              <div className="smallcaps mb-1">Hoteles</div>
              <ul className="text-[12px] space-y-0.5">
                {p.hotels.map((h, i) => {
                  const price = (p.pricing?.items?.Accommodations || []).find((x) => x.name === h.name);
                  return <li key={i} className="flex justify-between gap-3"><span>{h.name}{h.location ? ` · ${h.location}` : ""} · {h.nights} noche{h.nights === 1 ? "" : "s"}</span>{price && <span className="tabular text-clay-600">{fmtMoney(price.amount, price.currency)}</span>}</li>;
                })}
              </ul>
            </div>
          )}
          {(p.days || []).length > 0 && (
            <div>
              <button onClick={() => setShowDays(!showDays)} className="smallcaps inline-flex items-center gap-1 hover:text-terracotta" data-testid={`kimkim-proposal-${index}-days-toggle`}>
                {showDays ? <ChevronDown size={11}/> : <ChevronRight size={11}/>} Itinerario día a día ({p.days.length} días)
              </button>
              {showDays && (
                <ol className="mt-2 space-y-2">
                  {p.days.map((d) => (
                    <li key={d.n} className="text-[12px]">
                      <div className="font-medium">Día {d.n} · {d.title}{d.date ? <span className="text-clay-500 font-normal"> · {d.date}</span> : null}</div>
                      <ul className="ml-3 mt-0.5 space-y-0.5">
                        {(d.items || []).map((it, j) => {
                          const Icon = KIND_ICON[it.kind] || ChevronRight;
                          const price = (p.pricing?.items?.["Activities & Transportation"] || []).find((x) => x.name === it.name);
                          return <li key={j} className="flex items-start gap-1.5"><Icon size={11} className="mt-0.5 shrink-0 text-clay-500"/><span className="flex-1">{it.name}{it.meta ? <span className="text-clay-500"> · {it.meta}</span> : null}</span>{price && <span className="tabular text-clay-600">{fmtMoney(price.amount, price.currency)}</span>}</li>;
                        })}
                      </ul>
                    </li>
                  ))}
                </ol>
              )}
            </div>
          )}
          <a href={p.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider text-clay-600 hover:text-clay-900"><ExternalLink size={11}/> Abrir en KimKim</a>
        </div>
      )}
    </div>
  );
}

export function ProposalCards({ proposals, docs, budget }) {
  if (!proposals?.length) return null;
  const byUrl = Object.fromEntries((docs || []).map((d) => [d.url, d]));
  return (
    <section data-testid="kimkim-proposals">
      <div className="smallcaps mb-2">Propuestas enviadas ({proposals.length})</div>
      <div className="space-y-2">
        {proposals.map((p, i) => {
          const doc = byUrl[p.url];
          if (!doc) {
            return (
              <div key={p.url || i} className="border border-dashed border-clay-300 px-4 py-3 text-[12px] text-clay-600 flex justify-between gap-3" data-testid={`kimkim-proposal-${i}`}>
                <span>{p.title || "Propuesta"} · enviada {fmtWhen(p.sent_at)} · contenido pendiente de leer</span><span className="tabular">{fmtUsd(p.price_usd)}</span>
              </div>
            );
          }
          return <ProposalCard key={p.url} p={{ ...doc, sent_at: p.sent_at, total_usd: doc.total_usd ?? p.price_usd }} index={i} budget={budget}/>;
        })}
      </div>
    </section>
  );
}
